'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function lockDocumentScroll() {
  const root = document.documentElement;
  root.dataset.ssOverlayLocks = String(Number(root.dataset.ssOverlayLocks ?? '0') + 1);
  root.classList.add('ss-overlay-scroll-locked');
}

function unlockDocumentScroll() {
  const root = document.documentElement;
  const remainingLocks = Math.max(0, Number(root.dataset.ssOverlayLocks ?? '1') - 1);
  if (remainingLocks === 0) {
    delete root.dataset.ssOverlayLocks;
    root.classList.remove('ss-overlay-scroll-locked');
    return;
  }
  root.dataset.ssOverlayLocks = String(remainingLocks);
}

/**
 * A narrow-screen, route-owned filter sheet. It deliberately owns only modal
 * mechanics; the calling route owns filter values, labels, and mutations.
 */
export function MobileFilterSheet({
  isOpen,
  title,
  summary,
  children,
  onClose,
  footer,
}: {
  isOpen: boolean;
  title: string;
  summary?: ReactNode;
  children: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
}) {
  const sheetRef = useRef<HTMLElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const [isNarrowViewport, setIsNarrowViewport] = useState(() => (
    typeof window === 'undefined' || !window.matchMedia
      ? true
      : window.matchMedia('(max-width: 767px)').matches
  ));

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }

    const media = window.matchMedia('(max-width: 767px)');
    const update = () => setIsNarrowViewport(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!isOpen || !isNarrowViewport) return;
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    lockDocumentScroll();
    const frame = window.requestAnimationFrame(() => {
      sheetRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
      unlockDocumentScroll();
      if (openerRef.current?.isConnected) openerRef.current.focus({ preventScroll: true });
    };
  }, [isNarrowViewport, isOpen]);

  if (!isOpen || !isNarrowViewport) return null;

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = Array.from(sheetRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])
      .filter((element) => !element.hidden && element.getClientRects().length > 0);
    const first = items[0];
    const last = items.at(-1);
    if (!first || !last) {
      event.preventDefault();
      sheetRef.current?.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[var(--ss-layer-overlay)] md:hidden" data-mobile-filter-sheet="">
      <button
        type="button"
        aria-label={`Close ${title}`}
        className="ss-overlay-backdrop absolute inset-0 w-full"
        data-ss-overlay-kind="mobile-filter-sheet"
        onClick={onClose}
      />
      <section
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="ss-overlay-panel absolute inset-x-0 bottom-[calc(var(--ss-mobile-nav-clearance)+0.75rem+env(safe-area-inset-bottom))] flex max-h-[min(82dvh,calc(100dvh-var(--ss-mobile-nav-clearance)-1.5rem-env(safe-area-inset-top)-env(safe-area-inset-bottom)))] flex-col rounded-t-[1.75rem] border border-b-0 shadow-e3"
        style={{ maxHeight: 'min(82dvh, calc(100dvh - var(--ss-mobile-nav-clearance) - 1.5rem - env(safe-area-inset-top) - env(safe-area-inset-bottom)))' }}
        data-ss-overlay-material="transient"
      >
        <div className="ss-overlay-divider flex shrink-0 items-start justify-between gap-3 border-b px-4 pb-3 pt-3">
          <div className="min-w-0">
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-[var(--ss-border)]" aria-hidden="true" />
            <h2 className="text-base font-semibold text-[var(--ss-text)]">{title}</h2>
            {summary ? <div className="mt-1 text-sm text-[var(--ss-text-muted)]">{summary}</div> : null}
          </div>
          <button type="button" onClick={onClose} className="ss-icon-button text-[var(--ss-text-muted)]" aria-label={`Close ${title}`}>
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4" data-ss-overlay-scroll-region>{children}</div>
        {footer ? <div className="ss-overlay-divider shrink-0 border-t px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3">{footer}</div> : null}
      </section>
    </div>
    ,
    document.body,
  );
}
